import { FormInstance, FormProps, message, Row } from 'antd/lib'
import { FirmType } from '../../../../enums/management-input.enum.ts'
import { useTranslation } from 'react-i18next'
import Form from 'antd/es/form/index'
import IdeyaTypography from '../../../../common/antd/ideya-typography.tsx'
import Input from 'antd/es/input'
import { IdeyaSegmented } from '../../../../common/antd/ideya-segmented.tsx'
import { IdeyaButton } from '../../../../common/antd/ideya-button/ideya-button.tsx'
import { useAppSelector } from '../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../features/setting/setting.slice.ts'
import {
  useCreateFirmMutation,
  useUpdateFirmMutation,
} from '../../../../apis/firm-management/firm-management-api.api.ts'
import notification from 'antd/es/notification/index'
import { useCallback, useEffect } from 'react'
import { getErrorDescription } from '../../../../redux/utils.ts'
import { FirmListItem } from '../../../../apis/firm-management/firm-management-api.def.ts'
import Switch from 'antd/es/switch/index'

export type FirmManagementFormFieldTypes = {
  name: string
  type: FirmType.Internal | FirmType.External
  ranked: boolean
}

interface FirmManagementFormProps {
  editFirm?: FirmListItem
  mode: 'create' | 'edit'
  onCancel: () => void
  form: FormInstance<FirmManagementFormFieldTypes>
}

export const FirmManagementForm: React.FC<FirmManagementFormProps> = ({
  editFirm,
  mode,
  onCancel,
  form,
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const [messageApi, messageContextHolder] = message.useMessage()
  const [notificationApi, notificationContextHolder] =
    notification.useNotification()
  const [createFirm, { isLoading, isSuccess, isError, error, reset }] =
    useCreateFirmMutation()

  const [
    updateFirm,
    {
      isLoading: isUpdateLoading,
      isSuccess: isUpdateSuccess,
      isError: isUpdateError,
      error: updateError,
      reset: updateReset,
    },
  ] = useUpdateFirmMutation()

  const onFinish: FormProps<FirmManagementFormFieldTypes>['onFinish'] = (
    values
  ) => {
    createFirm(values)
  }

  const onUpdate: FormProps<FirmManagementFormFieldTypes>['onFinish'] = async (
    values
  ) => {
    await updateFirm({
      id: editFirm!.id,
      name: values.name,
      type: values.type,
      ranked: values.ranked,
    }).then(() => {
      onCancel()
    })
  }

  const success = useCallback(() => {
    messageApi.open({
      type: 'success',
      content: t('firm_management.form.success_message'),
    })
  }, [messageApi, t])

  const updateSuccess = useCallback(() => {
    messageApi.open({
      type: 'success',
      content: t('firm_management.form.update_success_message'),
    })
  }, [messageApi, t])

  const errorNotification = useCallback(
    (description: string) => {
      notificationApi.error({
        message: 'Error',
        description,
        placement: 'topRight',
      })
    },
    [notificationApi]
  )

  useEffect(() => {
    if (isSuccess) {
      success()
      form.resetFields()
      onCancel()
      reset()
    }

    if (isUpdateSuccess) {
      updateSuccess()
      onCancel()
      updateReset()
    }
  }, [
    isSuccess,
    form,
    onCancel,
    reset,
    success,
    isUpdateSuccess,
    updateSuccess,
    updateReset,
  ])

  useEffect(() => {
    if (isError) {
      const description = getErrorDescription(error)
      errorNotification(description)
      reset()
    }

    if (isUpdateError) {
      const description = getErrorDescription(updateError)
      errorNotification(description)
      updateReset()
    }
  }, [
    isError,
    error,
    reset,
    errorNotification,
    isUpdateError,
    updateError,
    updateReset,
  ])

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  return (
    <>
      {messageContextHolder}
      {notificationContextHolder}
      <Form
        form={form}
        layout={'vertical'}
        onFinish={mode === 'create' ? onFinish : onUpdate}
        initialValues={{ type: FirmType.External }}
      >
        <Form.Item
          label={t('firm_management.form.firm_name')}
          name={'name'}
          rules={[
            {
              required: true,
              message: t('firm_management.form.please_enter_firm_name'),
            },
          ]}
          extra={
            <IdeyaTypography.Text type={'secondary'}>
              {t('firm_management.form.capital_only')}
            </IdeyaTypography.Text>
          }
        >
          <Input
            placeholder={t('firm_management.form.firm_name')}
            onChange={(e) => {
              const lettersOnly = e.target.value
                .replace(/[^a-zA-Z0-9 ]/g, '')
                .toUpperCase()
              form.setFieldsValue({ name: lettersOnly })
            }}
          />
        </Form.Item>
        <Form.Item
          label={t('firm_management.form.type')}
          name={'type'}
          rules={[
            {
              required: true,
              message: t(
                'user_management.user_modal.form.please_select_account_type'
              ),
            },
          ]}
        >
          <IdeyaSegmented
            options={[
              {
                value: FirmType.External,
                label: t('firm_management.form.external'),
              },
              {
                value: FirmType.Internal,
                label: t('firm_management.form.internal'),
              },
            ]}
          />
        </Form.Item>
        <Form.Item
          label={t('firm_management.form.ranked')}
          name={'ranked'}
          initialValue={true}
        >
          <Switch />
        </Form.Item>
        <Row
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: themeStyle.marginXS,
            marginTop: themeStyle.marginXXL,
          }}
        >
          <IdeyaButton ghost onClick={handleCancel}>
            {t('common.cancel')}
          </IdeyaButton>
          <IdeyaButton
            loading={isLoading || isUpdateLoading}
            htmlType={'submit'}
          >
            {t('common.ok')}
          </IdeyaButton>
        </Row>
      </Form>
    </>
  )
}
